import {
  IAdminUser,
  IAgencyData,
  IAgentUser,
  IRegistrationData,
} from "../../@types/api-data.types";
import { LocalStorageEnums } from "../../enums/env.variables.enums";
import { LocalStorageKeys } from "./common.utils";

export class LocalStorageHelpers {
  static getAccessToken = () => {
    let rawToken = "";

    if (typeof window !== "undefined") {
      rawToken = localStorage?.getItem(
        LocalStorageKeys?.brand_temporary_access_token ||
          LocalStorageKeys?.brand_access_token,
      ) as string;
    }

    if (rawToken)
      try {
        return rawToken;
      } catch (e) {
        console.error(e);
      }

    return "";
  };

  static setAuthTokens = ({
    access_token,
    refresh_token,
    temporary_access_token,
  }: {
    access_token?: string;
    refresh_token?: string;
    temporary_access_token?: string;
  }) => {
    try {
      if (access_token)
        localStorage.setItem(
          LocalStorageEnums.access_token,
          access_token?.trim(),
        );

      if (refresh_token)
        localStorage.setItem(
          LocalStorageEnums.refresh_token,
          refresh_token?.trim(),
        );

      if (temporary_access_token)
        localStorage.setItem(
          LocalStorageKeys.brand_temporary_access_token,
          temporary_access_token?.trim(),
        );
    } catch (er) {
      console.error("Something went wrong while loading access token");
      console.error(er);
      return {};
    }
  };

  static getAuthTokens = () => {
    let accessToken = "";
    let refreshToken = "";
    let temporaryToken = "";

    try {
      accessToken = localStorage.getItem(LocalStorageEnums.access_token) ?? "";
      refreshToken =
        localStorage.getItem(LocalStorageEnums.refresh_token) ?? "";
      temporaryToken =
        localStorage.getItem(LocalStorageKeys.brand_temporary_access_token) ??
        "";

      return {
        access_token: accessToken,
        refresh_token: refreshToken,
        temporary_access_token: temporaryToken,
      };
    } catch (er) {
      console.error(
        "Something went wrong while retrieving data to local storage",
      );
      console.error(er);
      return {
        access_token: accessToken,
        refresh_token: refreshToken,
        temporary_access_token: temporaryToken,
      };
    }
  };

  static setAgentData = (params: { data: any }) => {
    try {
      localStorage.setItem(
        LocalStorageKeys.agent_data,
        JSON.stringify(params.data),
      );
    } catch (er) {
      console.error("Something went wrong while storing data to local storage");
      console.error(er);
    }
  };

  static getAgentData = (): IAgentUser | null => {
    try {
      return JSON.parse(
        localStorage.getItem(LocalStorageKeys.agent_data) ?? "",
      );
    } catch (er) {
      console.error(
        "Something went wrong while retrieving data to local storage",
      );
      console.error(er);
      return null;
    }
  };

  static setAdminData = (params: { data: any }) => {
    try {
      localStorage.setItem(
        LocalStorageKeys.admin_data,
        JSON.stringify(params.data),
      );
    } catch (er) {
      console.error("Something went wrong while storing data to local storage");
      console.error(er);
    }
  };

  static getAdminData = (): IAdminUser | null => {
    try {
      const data = localStorage.getItem(LocalStorageKeys.admin_data);
      return data ? JSON.parse(data) : null;
    } catch (er) {
      console.error("Error parsing admin_data", er);
      return null;
    }
  };

  static setAgencyData = (params: { data: any }) => {
    try {
      localStorage.setItem(
        LocalStorageKeys.agency_data,
        JSON.stringify(params.data),
      );
    } catch (er) {
      console.error("Something went wrong while storing data to local storage");
      console.error(er);
    }
  };

  static setUserData = (params: { data: any }) => {
    try {
      localStorage.setItem(
        LocalStorageKeys.user_data,
        JSON.stringify(params.data),
      );
    } catch (er) {
      console.error("Something went wrong while storing data to local storage");
      console.error(er);
    }
  };

  static getUserData = (): IAgencyData | null => {
    try {
      return JSON.parse(localStorage.getItem(LocalStorageKeys.user_data) ?? "");
    } catch (er) {
      console.error(
        "Something went wrong while retrieving data to local storage",
      );
      console.error(er);
      return null;
    }
  };

  static isSuperAdmin = (): boolean => {
    const userData: any = LocalStorageHelpers.getUserData();

    console.log("--- DEBUG SUPER ADMIN CHECK ---");
    console.log("Full User Data:", userData);
    console.log("User Role Type:", typeof userData?.user_role);
    console.log("User Role Value:", userData?.user_role);

    if (!userData || !userData.user_role) {
      console.log("❌ No user data or role found");
      return false;
    }

    if (typeof userData.user_role === "object") {
      if (userData.user_role.slug === "super-admin") {
        console.log("✅ Allowed: Matched by slug 'super-admin'");
        return true;
      }

      if (userData.user_role._id === "67aca623ef6170851a306d55") {
        console.log("✅ Allowed: Matched by ID inside object");
        return true;
      }
    }

    // 2. Check if user_role is just a String (ID Only - Not populated)
    // Sometimes LocalStorage holds just the ID string if the user details endpoint hasn't updated it yet
    if (typeof userData.user_role === "string") {
      if (userData.user_role === "67aca623ef6170851a306d55") {
        console.log("✅ Allowed: Matched by ID string");
        return true;
      }
    }

    console.log("❌ Denied: Role did not match super-admin criteria");
    return false;
  };

  static getAgencyData = (): IAgencyData | null => {
    try {
      return JSON.parse(
        localStorage.getItem(LocalStorageKeys.agency_data) ?? "",
      );
    } catch (er) {
      console.error(
        "Something went wrong while retrieving data to local storage",
      );
      console.error(er);
      return null;
    }
  };

  static setRegistrationData = (params: { data: any }) => {
    try {
      localStorage.setItem(
        LocalStorageKeys.registration_data,
        JSON.stringify(params.data),
      );
    } catch (er) {
      console.error("Something went wrong while storing data to local storage");
      console.error(er);
    }
  };

  static getRegistrationData = (): IRegistrationData | null => {
    try {
      return JSON.parse(
        localStorage.getItem(LocalStorageKeys.registration_data) ?? "",
      );
    } catch (er) {
      console.error("Something went wrong while storing data to local storage");
      console.error(er);
      return null;
    }
  };

  static clear = () => {
    localStorage.clear();
  };
}
